import { useEffect, useState } from 'react';
import type { FoundryGameStateSnapshot } from '@volley/crucible/foundry/state-kernel.js';

type BoundSnapshot = {
  gameId: string;
  snapshot: FoundryGameStateSnapshot;
};

function shouldApplySnapshot(
  current: FoundryGameStateSnapshot | undefined,
  next: FoundryGameStateSnapshot,
): boolean {
  if (!current) return true;
  if (current.sourceRevision === next.sourceRevision) return false;
  return Date.parse(next.generatedAt) >= Date.parse(current.generatedAt);
}

export function useFoundryGameStateSnapshot(
  gameId: string | undefined,
): FoundryGameStateSnapshot | undefined {
  const [bound, setBound] = useState<BoundSnapshot | undefined>(undefined);

  useEffect(() => {
    if (!gameId) {
      setBound(undefined);
      return;
    }

    setBound(undefined);
    let cancelled = false;

    const applySnapshot = (next: FoundryGameStateSnapshot) => {
      setBound((current) => {
        const previous =
          current !== undefined && current.gameId === gameId ? current.snapshot : undefined;
        if (!shouldApplySnapshot(previous, next)) return current;
        return { gameId, snapshot: next };
      });
    };

    const unsubscribePush = window.crucible.on('game-state:update', (payload) => {
      if (cancelled) return;
      if (payload.gameId !== gameId) return;
      applySnapshot(payload.snapshot);
    });

    void window.crucible
      .invoke('game-state:subscribe', { gameId })
      .then((current) => {
        if (cancelled) return;
        applySnapshot(current);
      })
      .catch(() => {
        // Snapshot subscription failures degrade to the legacy Workspace
        // derivation until main can answer again.
      });

    return () => {
      cancelled = true;
      unsubscribePush();
      void window.crucible.invoke('game-state:unsubscribe', { gameId }).catch(() => {
        // best effort; main tears down subscriptions on process exit
      });
    };
  }, [gameId]);

  // Pin the returned snapshot to the active gameId. useEffect clears state
  // post-commit, so the first render after a route change would otherwise
  // still expose the previous game's kernel snapshot to Workspace.
  return bound !== undefined && bound.gameId === gameId ? bound.snapshot : undefined;
}
